import { useEffect, useState } from "react";
import { PaperPlaneTilt, CircleNotch, WifiSlash, WarningCircle } from "@phosphor-icons/react";
import { fmtBRL } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

export const useOnline = () => {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
};

export const NfeConfirmDialog = ({ form, ambiente, origem, busy, onConfirm, onClose }) => {
  const online = useOnline();
  const d = form.destinatario || {};
  const doc = String(d.cnpj_cpf || "").replace(/\D/g, "");
  const ieMissing = !String(d.ie || "").trim() && doc.length === 14;
  const itens = (form.itens || []).filter((it) => (it.descricao || "").trim());
  const total = parseFloat(String(form.impostos?.valor_total ?? "").replace(",", ".")) || 0;
  const prod = ambiente === "producao";

  return (
    <Dialog open onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent data-testid="nfe-confirm-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-50 max-w-md max-h-[92dvh] overflow-y-auto p-5">
        <DialogHeader>
          <DialogTitle className="font-display">Confirmar emissão da NF-e</DialogTitle>
          <DialogDescription className={prod ? "text-rose-300" : "text-zinc-500"}>
            {origem} · ambiente de {prod ? "PRODUÇÃO (valor fiscal real)" : "homologação (teste, sem valor fiscal)"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="border border-zinc-800 rounded-lg p-3.5 bg-zinc-900/40 space-y-1.5">
            <p className="flex justify-between gap-3"><span className="text-zinc-500">Cliente</span>
              <span data-testid="confirm-cliente" className="text-right font-medium">{d.nome || "—"}</span></p>
            <p className="flex justify-between gap-3"><span className="text-zinc-500">CNPJ/CPF</span>
              <span>{d.cnpj_cpf || "—"}</span></p>
            <p className="flex justify-between gap-3"><span className="text-zinc-500">Itens</span>
              <span data-testid="confirm-itens">{itens.length}</span></p>
            {itens.slice(0, 4).map((it, i) => (
              <p key={`${it.descricao || "item"}-${i}`} className="text-xs text-zinc-400 truncate">• {it.quantidade}x {it.descricao}</p>
            ))}
            {itens.length > 4 && <p className="text-xs text-zinc-500">… e mais {itens.length - 4} item(ns)</p>}
            <p className="flex justify-between gap-3 border-t border-zinc-800 pt-1.5 mt-1">
              <span className="text-zinc-500">Valor total</span>
              <span data-testid="confirm-total" className="font-semibold text-indigo-300">{fmtBRL(total)}</span></p>
          </div>

          {ieMissing && (
            <p data-testid="confirm-ie-warning" className="flex gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded px-3 py-2.5">
              <WarningCircle size={16} className="shrink-0 mt-0.5" />
              Destinatário CNPJ sem Inscrição Estadual e sem marcação de Isento — a nota será emitida como NÃO CONTRIBUINTE (indicador 9).
            </p>
          )}

          <p className="text-xs text-zinc-500">
            A emissão é enviada à SEFAZ e não pode ser desfeita facilmente — apenas por cancelamento formal dentro do prazo legal.
          </p>

          {!online && (
            <p data-testid="confirm-offline-warning" className="flex gap-2 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2.5">
              <WifiSlash size={17} className="shrink-0 mt-0.5" />
              Sem conexão com a internet. A emissão de NF-e exige internet — conecte-se e tente novamente.
            </p>
          )}
        </div>

        <div className="flex gap-2 mt-4">
          <button data-testid="confirm-emit-button" onClick={onConfirm} disabled={busy || !online}
            className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 active:scale-[0.99] disabled:opacity-50 text-white text-sm font-semibold px-4 py-3 min-h-[48px] rounded-lg transition-all">
            {busy ? <CircleNotch size={17} className="animate-spin" /> : <PaperPlaneTilt size={17} />} Confirmar Emissão
          </button>
          <button data-testid="confirm-cancel-button" onClick={onClose} disabled={busy}
            className="border border-zinc-700 hover:bg-zinc-800 text-zinc-400 text-sm font-medium px-4 py-3 min-h-[48px] rounded-lg transition-colors">
            Voltar
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
